import RichText from '@/components/RichText';
import {
  SectionContainer,
  WrapperSectionContainer,
  WrapperTitle,
  WrapperTitleUnderline,
} from '@/components/Wrapper/Wrapper.styles';
import { useGlobalState } from '@/hooks';
import { type IRichText, WrapperBackgroundColor } from '@/interfaces';
import { extractTextFromNode, groupBlocksByHeading } from '@/utils/seoPageSchemas.utils';
import { BLOCKS, type Document, type TopLevelBlock } from '@contentful/rich-text-types';
import { useState } from 'react';
import { Answer, Chevron, Item, List, QuestionButton, QuestionHeading, QuestionText } from './FaqAccordion.styles';

// A page carries one FAQ, so fixed ids stay unique and match between server and client render.
const ID_PREFIX = 'seo-faq';

// The FAQ field is a bare rich-text document: no embedded assets or entries, so nothing to link.
const NO_LINKS: IRichText['links'] = { assets: { block: [] }, entries: { block: [] } };

const renderBlocks = (blocks: TopLevelBlock[]) => (
  <RichText
    richText={{ json: { nodeType: BLOCKS.DOCUMENT, data: {}, content: blocks } as Document, links: NO_LINKS }}
  />
);

export interface FaqAccordionProps {
  title: string;
  /** Growth writes each question as a heading with its answer underneath (see `extractFaqItemsFromRichText`). */
  content: Document;
}

/**
 * SEO-page FAQ, one collapsible row per question. Collapsed answers are hidden, not unmounted, so
 * the server-rendered HTML keeps every answer for crawlers and the FAQPage schema.
 */
const FaqAccordion: React.FC<FaqAccordionProps> = ({ title, content }) => {
  const { t } = useGlobalState();
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

  const blocks = content.content ?? [];
  const groups = groupBlocksByHeading(blocks);
  // Blocks above the first question render as a lead-in.
  const intro = groups.length ? blocks.slice(0, blocks.indexOf(groups[0].heading as TopLevelBlock)) : blocks;

  if (!blocks.length) return null;

  const toggle = (index: number) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(index)) next.add(index);
      return next;
    });

  return (
    <WrapperSectionContainer bgColor={WrapperBackgroundColor.white}>
      <SectionContainer>
        <WrapperTitle type='h2'>{t(title)}</WrapperTitle>
        <WrapperTitleUnderline />
        {intro.length > 0 && renderBlocks(intro)}

        {groups.length > 0 && (
          <List>
            {groups.map(({ heading, body }, index) => {
              const isExpanded = expanded.has(index);
              const answerId = `${ID_PREFIX}-answer-${index}`;

              return (
                <Item key={index}>
                  <QuestionHeading>
                    <QuestionButton
                      type='button'
                      aria-expanded={isExpanded}
                      aria-controls={answerId}
                      onClick={() => toggle(index)}
                    >
                      <QuestionText type='span'>{extractTextFromNode(heading)}</QuestionText>
                      <Chevron $expanded={isExpanded} aria-hidden />
                    </QuestionButton>
                  </QuestionHeading>
                  <Answer id={answerId} hidden={!isExpanded}>
                    {renderBlocks(body)}
                  </Answer>
                </Item>
              );
            })}
          </List>
        )}
      </SectionContainer>
    </WrapperSectionContainer>
  );
};

export default FaqAccordion;
